import React, { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, fmtNum } from "../components/Shared";
import { toast } from "sonner";
import {
  Loader2, AlertTriangle, CalendarClock, PackageX, MapPin, Warehouse, RotateCcw,
  FileSpreadsheet, FileText,
} from "lucide-react";

const TABS = [
  { key: "expiring", label: "Akan Kadaluarsa (≤ 3 Bulan)", icon: CalendarClock },
  { key: "missing", label: "Stok Awal Ada, Fisik Tidak Ditemukan", icon: PackageX },
];

function expBadge(days, isExpired) {
  if (isExpired) return { label: "Sudah Expired", cls: "bg-red-100 text-red-700 border-red-200" };
  if (days <= 30) return { label: `${days} hari lagi`, cls: "bg-red-50 text-red-600 border-red-200" };
  return { label: `${days} hari lagi`, cls: "bg-amber-50 text-amber-700 border-amber-200" };
}

export default function Temuan() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("expiring");
  const [resetting, setResetting] = useState(false);
  const [exporting, setExporting] = useState("");

  const doExport = async (type) => {
    setExporting(type);
    try {
      const res = await api.get(`/temuan/export`, { params: { type }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "excel" ? "Hasil_Temuan_SO.xlsx" : "Hasil_Temuan_SO.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Hasil temuan ${type === "excel" ? "Excel" : "PDF"} berhasil diunduh`);
    } catch (e) {
      toast.error("Gagal mengekspor hasil temuan");
    } finally {
      setExporting("");
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/temuan").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (!window.confirm("Bersihkan hasil temuan? Ini akan MENGHAPUS SEMUA data hasil hitung fisik stock opname (semua tempat) dan MEMBUKA kunci periode SO bila sedang terkunci. Saldo Master & progress ikut kosong. Lanjutkan?")) return;
    if (!window.confirm("Konfirmasi sekali lagi: data hitungan fisik tidak dapat dikembalikan. Yakin mau reset?")) return;
    setResetting(true);
    try {
      const { data } = await api.delete("/opname/reset");
      toast.success(`Hasil temuan dibersihkan (${data.deleted} data direset, periode dibuka)`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  const expiring = data?.expiring || [];
  const missing = data?.missing || [];

  return (
    <div className="pb-12">
      <PageHeader title="Hasil Temuan Stock Opname" subtitle="Obat mendekati kadaluarsa & stok yang tidak ditemukan saat perhitungan fisik">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => doExport("excel")} disabled={exporting === "excel"} data-testid="temuan-export-excel-btn" className="inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-white px-3.5 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50 disabled:opacity-60">
            {exporting === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Export Excel
          </button>
          <button onClick={() => doExport("pdf")} disabled={exporting === "pdf"} data-testid="temuan-export-pdf-btn" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Export PDF
          </button>
          {isAdmin && (
            <button onClick={doReset} disabled={resetting} data-testid="temuan-reset-btn" className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Bersihkan Hasil Temuan
            </button>
          )}
        </div>
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-amber-100 p-5" data-testid="temuan-expiring-stat">
            <div className="flex items-center gap-2 text-amber-700"><CalendarClock className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">Akan Kadaluarsa ≤ 3 Bulan</span></div>
            <div className="mt-2 font-head text-3xl font-extrabold text-amber-700 tabular">{fmtNum(data?.expiring_count ?? 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-red-100 p-5" data-testid="temuan-missing-stat">
            <div className="flex items-center gap-2 text-red-600"><PackageX className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">Stok Awal Ada, Fisik 0</span></div>
            <div className="mt-2 font-head text-3xl font-extrabold text-red-600 tabular">{fmtNum(data?.missing_count ?? 0)}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`temuan-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  tab === t.key ? "bg-teal-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-4 py-16 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : tab === "expiring" ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-left">
                    {["Nama Obat", "ID Barang", "Satuan", "Tempat Penyimpanan", "Keterangan", "Kadaluarsa", "Qty Fisik", "Status"].map((h, i) => (
                      <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {expiring.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-16 text-center">
                      <CalendarClock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Tidak ada obat yang akan kadaluarsa dalam 3 bulan ke depan.</p>
                    </td></tr>
                  ) : expiring.map((m, i) => {
                    const b = expBadge(m.days_left, m.is_expired);
                    return (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid="temuan-expiring-row">
                        <td className="px-4 py-2.5 font-medium text-slate-800 max-w-xs">{m.nama}</td>
                        <td className="px-4 py-2.5 font-mono-t text-xs text-slate-500">{m.id_barang || "-"}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 uppercase">{m.satuan_jual || "-"}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-600 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" />{m.lokasi_penyimpanan || "-"}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-600">{m.keterangan || "-"}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-600">{m.tanggal_expired}</td>
                        <td className="px-4 py-2.5 text-right tabular font-mono-t text-slate-700">{m.quantity_fisik === null ? "—" : fmtNum(m.quantity_fisik)}</td>
                        <td className="px-4 py-2.5"><span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-semibold ${b.cls}`}>{b.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex items-start gap-2 bg-red-50 border-b border-red-100 px-4 py-2 text-xs text-red-700">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Obat berikut punya <b>saldo awal &gt; 0</b> namun <b>total qty fisik = 0</b> setelah dilakukan perhitungan (tidak terhitung / fisik tidak ada). Perlu ditindaklanjuti.</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-left">
                    {["Nama Obat", "ID Barang", "Satuan", "Gudang", "Qty Awal (Saldo)", "Qty Fisik", "Kelengkapan"].map((h, i) => (
                      <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {missing.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-16 text-center">
                      <PackageX className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Tidak ada temuan. Semua obat dengan stok awal ditemukan fisiknya.</p>
                    </td></tr>
                  ) : missing.map((m, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid="temuan-missing-row">
                      <td className="px-4 py-2.5 font-medium text-slate-800 max-w-xs">{m.nama}</td>
                      <td className="px-4 py-2.5 font-mono-t text-xs text-slate-500">{m.id_barang || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 uppercase">{m.satuan_jual || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-600 flex items-center gap-1.5"><Warehouse className="h-3.5 w-3.5 text-slate-400" />{m.gudang || "-"}</td>
                      <td className="px-4 py-2.5 text-right tabular font-mono-t text-slate-700">{fmtNum(m.total_awal)}</td>
                      <td className="px-4 py-2.5 text-right tabular font-mono-t font-bold text-red-600">0</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{m.counted_locations}/{m.total_locations} tempat</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
